import { checkOrderDetails, editOrderDetails } from '@/api/orders.js';
import PriceBreakDownDetails from '@/components/EditOrder/PriceBreakDownDetails.jsx';
import { usePrebook } from '@/hooks/usePrebook.js';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import LoaderStore from '@/store/loader.js';
import { mockCheckoutData } from '@/mocks/fullPageCartMocks';
import { getCurrentDayTitle, formatPrebookDate } from '@/utils/dates/dates.js';
import { useTranslations } from 'next-intl';
import { getPaymentMethods } from '@/utils/getConfiguration';
import { PaymentOptionStore } from '@/store/sideSheet';
import { getPaymentCard } from '@/utils/products/products';
import {
  addStickers,
  submitProducts,
  ACCEPTED_STATE,
  checkOrderEdit,
  setCartProducts,
} from '@/utils/products/products';
import { checkoutStore, fullCartStore, productsForOrder } from '@/store/cart';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useContext } from 'react';
import {
  SingleLineOption,
  AccordionContent,
  Clock,
  TextArea,
  Heading,
  HeadingEnum,
  BoldEnum,
  Button,
  PrebookTimeSlots,
  Checkout,
  CheckoutTimeSlots,
} from '@desquared/wave-components-library';
import * as styles from './DeliveryPaymentDetails.module.css';
import { submitOrderEditCart } from '@/api/wave/orders';
import PrebookEditOrderStore from '@/store/prebookEditOrder';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useGenericTimeslots from '@/hooks/useGenericTimeslots';
import {
  fetchTimeslotsTakeAway,
  fetchTimeslotsDelivery,
  changeSelectedIndexes,
  EXACT_DATE_TIMESLOT_TOKEN,
  PREDICTION_TIMESLOT_TOKEN,
  createPredictiveTimeslotTextWithDate,
  createTimeslotsProducts,
} from '@/utils/logic/timeslots';
import { PostalCode, SelectedShopId } from '@/store/isAddressRegistered';
import { formatUTCTimeslotsToLocal } from '@/utils/dates/dates.js';
import { transfromTimeslots } from '@/utils/transformers/transformTimeslots';
import { distinctUntilChanged } from 'rxjs/operators';
import { isEmpty } from 'lodash';
import { GeneralSelectedTimeslotStore, FirstSelectedTimeslotStore } from '@/store/selectedTimeslot';
import { useDateFormatting } from '@/contexts/DateFormattingProvider';
import { useConfiguration } from '@/contexts/ConfigContext';
import { UserIsOfLegalAgeStore } from '@/store/userIsOfLegalAge.js';
import { useIsRtl } from '@/contexts/DirectionContext.jsx';
import { constructAddressLine, calculateOrderLimit } from '@/utils/products/products';

export default function DeliveryPaymentDetails({
  selectedOrder,
  onClose,
  checkDetailsOnly = false,
  onUpdateOrder,
  isAddOnly = false,
}) {
  const t = useTranslations();
  const a = useTranslations('common.asides');
  const router = useRouter();
  // const paymentDropdownOptions = getPaymentMethods(t('checkout.save_card')) ?? mockCheckoutData.methods;
  const [comment, setComment] = useState(selectedOrder?.comments || '');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState();
  const [details, setDetails] = useState(false);
  const [showTimeslots, setShowTimeslots] = useState(false);
  const { bundleOffersSummary } = useBundleOffersSummary();
  const [saveCard, setSaveCard] = useState(false);
  const [updatedOrder, setUpdatedOrder] = useState(selectedOrder);
  const [postalCode, setPostalCode] = useState('');
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [genericTimeslots, setGenericTimeslots] = useState({});
  const [genericTransformedTimeslots, setGenericTransformedTimeslots] = useState({});
  const [genericIndexes, setGenericIndexes] = useState({});
  const [message, setMessage] = useState('');
  const [previousIndexes, setPreviousIndexes] = useState({});
  const { timeFormat } = useDateFormatting();
  const isRtl = useIsRtl();
  const { config, requiresAddressLine } = useConfiguration();
  const [isTimeslotsLoading, setIsTimeslotsLoading] = useState(true);

  const { addressPickerStatus, selectedUserAddressIndex, getCollectionTypes, deliveryServices } =
    useContext(DeliveryServicesContext);

  const selectedService = deliveryServices
    ? deliveryServices.find(i => i.id === selectedOrder?.deliveryService)
    : undefined;

  const [paymentDropdownOptions, setPaymentDropdownOptions] = useState(
    getPaymentMethods(t('checkout.save_card'), selectedService) ?? mockCheckoutData.methods
  );

  const [timeslotDropdownOptions, setTimeslotDropdownOptions] = useState([
    {
      name: t('common.timeslots.unsupported_region'),
      unavailable: true,
    },
  ]);
  const [paymentIndex, setPaymentIndex] = useState(
    paymentDropdownOptions.findIndex(i => i.type === selectedOrder.paymentType)
  );
  const choosePaymentOption = i => {
    setPaymentIndex(i);
    PaymentOptionStore.update(paymentDropdownOptions[i]);
    const newSelectedOrder = { ...selectedOrder, paymentType: paymentDropdownOptions[i].type };
    if (checkDetailsOnly) {
      handleCheckDetailsSubmit(false, newSelectedOrder);
    } else {
      checkOrderEdit(
        newSelectedOrder?._id,
        setCartProducts(productsForOrder.getValue()),
        newSelectedOrder,
        bundleOffersSummary,
        isAddOnly,
        undefined,
        undefined,
        t('full_page_cart.no_comment'),
        t('common.asides.side_cart.unavailable_product'),
        a
      ).then(result => {
        if (result && result.data) {
          fullCartStore.update(result);
          setData(result);
        }
      });
    }
  };

  useEffect(() => {
    if (!selectedService) return;
    const updatedOptions = getPaymentMethods(t('checkout.save_card'), selectedService) ?? mockCheckoutData.methods;
    setPaymentDropdownOptions(updatedOptions);
  }, [selectedService, t]);

  const {
    fetchTimeSlotsPerService,
    timeInMinsLeft,
    handlePrebookTimeslot,
    timeslots,
    handleSelectDay,
    handleSelectTimeslot,
    selectedDayWithPrebookStatus,
    prebook,
    hasPredictiveTimeslot,
    predictiveTimeslotText,
  } = usePrebook(PrebookEditOrderStore);

  const isPrebookingEnabled = selectedService?.timeslots?.prebook?.enabled;

  useEffect(() => {
    LoaderStore.setIsVisible(isLoading);
  }, [isLoading]);

  useEffect(() => {
    PrebookEditOrderStore.update({
      reservation: {
        startsAt: selectedOrder.timeSlotStart,
        endsAt: selectedOrder.timeSlotEnd,
      },
    });
  }, []);

  useEffect(() => {
    if (selectedOrder?._id && selectedOrder.deliveryService && selectedOrder.shopId) {
      if (!config || config?.timeslots?.calculation?.strategy === 'USING_CART_INFO') return;

      fetchTimeSlotsPerService(
        selectedOrder.deliveryService,
        selectedOrder.shopId,
        selectedOrder?.address?.postalCode,
        requiresAddressLine ? constructAddressLine(selectedOrder?.address) : undefined
      );
    }
  }, [selectedOrder?._id, data, config]);

  const getTimeSlotsPrebookMessage = () => {
    let msg = t('common.asides.edit_order.choose_delivery');
    const hasSelectedTimeslot = prebook?.reservation;
    if (hasSelectedTimeslot) {
      msg = `${getCurrentDayTitle(
        prebook?.reservation?.startsAt,
        router.locale,
        t('common.days.today')
      )} ${formatPrebookDate(prebook.reservation?.endsAt)}, ${formatPrebookDate(
        prebook?.reservation.startsAt,
        timeFormat
      )} -  ${formatPrebookDate(prebook?.reservation.endsAt, timeFormat)}`;
    }

    return msg;
  };
  const handleCheckDetailsSubmit = async (submit = true, newSelectedOrder = selectedOrder) => {
    if (!newSelectedOrder) return;
    checkoutStore.update(undefined);
    UserIsOfLegalAgeStore.clear();

    const submitValues = {
      paymentType: PaymentOptionStore.getValue() ? PaymentOptionStore.getValue().type : newSelectedOrder.paymentType,
      timeSlotStart: prebook?.reservation?.startsAt,
      timeSlotEnd: prebook?.reservation?.endsAt,
      comments: comment,
      cardId: newSelectedOrder?.cardId || getPaymentCard(),
    };

    try {
      setIsLoading(true);
      const checkData = await checkOrderDetails(newSelectedOrder._id, submitValues);

      setUpdatedOrder(checkData?.order);
      if (checkData.isValidForSubmit && submit) {
        const data = await editOrderDetails(newSelectedOrder._id, submitValues);
        if (data) {
          const newResult = { ...data, edit: true };
          checkoutStore.update(newResult);
          if (newResult?.paymentUrl && newResult?.paymentUrl?.length > 0) {
            // router.push(newResult.paymentUrl);
            window.location.href = data.paymentUrl;
          } else {
            router.replace('/thank-you');
          }
          onUpdateOrder(data);
          onClose(true);
        }
      }
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    if (!selectedOrder) return;
    checkoutStore.update(undefined);
    UserIsOfLegalAgeStore.clear();

    submitOrderEditCart(
      addStickers(submitProducts(isAddOnly)),
      selectedOrder?.shopId,
      selectedOrder?.deliveryService,
      selectedOrder?.address,
      selectedOrder?.wantPlasticBags,
      [
        prebook?.reservation?.endsAt || selectedOrder?.timeSlotEnd,
        prebook?.reservation?.startsAt || selectedOrder?.timeSlotStart,
      ],
      selectedOrder?.collectionTypes,
      (selectedOrder?.paymentOptions || []).filter(el => el?.visibleToUser),
      paymentDropdownOptions[paymentIndex].type,
      selectedOrder?.bottlesReturnInfo,
      selectedOrder?.invoice ?? undefined,
      selectedOrder?.userTimezone,
      comment,
      selectedOrder?.cardId || getPaymentCard(),
      saveCard,
      selectedOrder?.changesAllowed,
      selectedOrder?._id,
      isAddOnly
    )
      .then(result => {
        if (result && (result.status === ACCEPTED_STATE || result.status === 'PAYMENT_PENDING')) {
          const newResult = { ...result, edit: true };
          checkoutStore.update(newResult);

          if (result?.paymentUrl && result?.paymentUrl?.length > 0) {
            // router.push(result.paymentUrl);
            window.location.href = result.paymentUrl;
          } else {
            router.replace('/thank-you');
          }
        }
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isPrebookingEnabled) {
      setIsTimeslotsLoading(false);
      return;
    }

    PostalCode.lazyInit();
    const selectedShopId$ = SelectedShopId.subscribe(setSelectedShopId);
    const postalCode$ = PostalCode.subject.pipe(distinctUntilChanged()).subscribe(async value => {
      setIsTimeslotsLoading(true);
      try {
        if (selectedService?.deliversToAddress) {
          if (!config) return;
          if (selectedOrder?.address?.postalCode || (selectedOrder?.address && selectedOrder?.shop)) {
            let newEasyTimeslots = [];

            if (config?.timeslots?.calculation?.strategy === 'USING_CART_INFO') {
              newEasyTimeslots = await fetchTimeslotsDelivery(
                selectedOrder?.address?.postalCode,
                true,
                setGenericIndexes,
                selectedService?.id,
                selectedOrder.shopId,
                true,
                createTimeslotsProducts(submitProducts(isAddOnly)),
                requiresAddressLine ? constructAddressLine(selectedOrder?.address) : undefined
              );
            } else {
              newEasyTimeslots = await fetchTimeslotsDelivery(
                selectedOrder?.address?.postalCode,
                true,
                setGenericIndexes,
                selectedService?.id,
                selectedOrder.shopId,
                false,
                undefined,
                requiresAddressLine ? constructAddressLine(selectedOrder?.address) : undefined
              );
            }

            if (newEasyTimeslots.type === EXACT_DATE_TIMESLOT_TOKEN) {
              const localTimeslots = formatUTCTimeslotsToLocal(newEasyTimeslots.exactTimeTimeslots, timeFormat);
              setGenericTimeslots(localTimeslots);
              const newTransformedTimeslots = transfromTimeslots(localTimeslots, t);
              setGenericTransformedTimeslots(newTransformedTimeslots);
            }
          }
        } else if (selectedService) {
          if (!config) return;

          let takeAwayTimeslots;
          if (config?.timeslots?.calculation?.strategy === 'USING_CART_INFO') {
            takeAwayTimeslots = await fetchTimeslotsTakeAway(
              selectedService?.id,
              true,
              createTimeslotsProducts(submitProducts(isAddOnly))
            );
          } else {
            takeAwayTimeslots = await fetchTimeslotsTakeAway(selectedService?.id);
          }

          if (takeAwayTimeslots) {
            const localTimeslots = formatUTCTimeslotsToLocal(takeAwayTimeslots.exactTimeTimeslots, timeFormat);
            setGenericTimeslots(localTimeslots);
            const newTransformedTimeslots = transfromTimeslots(localTimeslots, t);
            setGenericTransformedTimeslots(newTransformedTimeslots);
            changeSelectedIndexes(localTimeslots, setGenericIndexes, setMessage);
          }
        }
      } catch (error) {
        console.error('Error fetching timeslots:', error);
      } finally {
        setIsTimeslotsLoading(false);
      }
    });
    return () => {
      postalCode$.unsubscribe();
    };
  }, [addressPickerStatus, postalCode, selectedShopId, selectedService, router.locale, config]);

  useGenericTimeslots(
    true,
    showTimeslots,
    addressPickerStatus,
    genericTimeslots,
    genericTransformedTimeslots,
    genericIndexes,
    setGenericIndexes,
    setMessage,

    setTimeslotDropdownOptions,
    () => {},
    selectedShopId,
    isPrebookingEnabled,
    t
  );

  function onChangeDayIndex(index) {
    setPreviousIndexes(genericIndexes);
    let newIndexes = { ...genericIndexes };
    newIndexes.selectedDayIndex = index;
    index === previousIndexes.selectedDayIndex
      ? (newIndexes.selectedIndex = previousIndexes.selectedIndex)
      : (newIndexes.selectedIndex = -1);

    setGenericIndexes(newIndexes);
  }

  function onChangeTimeslotIndex(index) {
    const transformedTimeslots = genericTransformedTimeslots[genericIndexes.selectedDayIndex];
    let newIndexes = { ...genericIndexes, selectedIndex: index };
    if (transformedTimeslots.timeslots[index].unavailable) return;
    setGenericIndexes(newIndexes);
    GeneralSelectedTimeslotStore.update(newIndexes);
  }
  return (
    <div className={styles.wrapper}>
      <div className={styles.body}>
        <div className={styles.section}>
          <Heading type={HeadingEnum.HeadingXS} bold={BoldEnum.Bold} classOption={styles.sectionTitle}>
            {t('common.asides.edit_order.service_details')}
          </Heading>
          <div className={styles.sectionContent}>
            {hasPredictiveTimeslot ? (
              <SingleLineOption
                title={predictiveTimeslotText[0]}
                isSelected={false}
                description={predictiveTimeslotText[1]}
                leftIcon={Clock}
                isLoading={isTimeslotsLoading}
              />
            ) : (
              <AccordionContent
                isOpen={showTimeslots}
                handleToggle={() => setShowTimeslots(!showTimeslots)}
                title={
                  <SingleLineOption
                    classOption={styles.accordionOption}
                    title={t('common.asides.edit_order.date_and_time')}
                    isSelected={false}
                    description={isPrebookingEnabled ? getTimeSlotsPrebookMessage() : timeslotDropdownOptions[0].name}
                    leftIcon={Clock}
                    isLoading={isTimeslotsLoading}
                  />
                }
              >
                <div className={styles.prebookContainer}>
                  {!isPrebookingEnabled && !isEmpty(genericTransformedTimeslots) && !isEmpty(genericIndexes) && (
                    <CheckoutTimeSlots
                      days={genericTransformedTimeslots}
                      selectedDayIndex={genericIndexes.selectedDayIndex}
                      setSelectedDayIndex={index => onChangeDayIndex(index)}
                      selectedTimeslotIndex={genericIndexes.selectedIndex}
                      setSelectedTimeslotIndex={index => onChangeTimeslotIndex(index)}
                      isRtl={isRtl}
                      // setSelectedTimeslotIndex={onChangeTimeslotIndex}
                    />
                  )}
                  {isPrebookingEnabled && prebook?.selectedDay && (
                    <>
                      <PrebookTimeSlots
                        formatTitle={d => getCurrentDayTitle(d, router.locale, t('common.days.today'))}
                        labels={{
                          title: t('common.asides.prebook.time_info_saved', {
                            minutes: selectedService?.timeslots?.prebook?.timeAllocationInMinutes,
                          }),
                          timeMessageForSavedValue: t('common.asides.prebook.saved_info'),
                          timeMessage: t('common.asides.prebook.time_info', {
                            minutes: selectedService?.timeslots?.prebook?.timeAllocationInMinutes,
                          }),
                          lowAvailability: t('common.asides.prebook.low_availability'),
                          unavailable: t('common.asides.prebook.unavailable'),
                        }}
                        formatDay={formatPrebookDate}
                        days={timeslots}
                        lowAvailabilityThreshold={2}
                        timeLeftInMins={timeInMinsLeft}
                        selectedDay={selectedDayWithPrebookStatus}
                        setSelectedDay={handleSelectDay}
                        selectedTimeslot={prebook?.selectedTimeslot}
                        setSelectedTimeslot={handleSelectTimeslot}
                        isRtl={isRtl}
                      />
                      <div className={styles.prebookButton}>
                        <Button
                          text={t('common.asides.edit_order.choose')}
                          disabled={!prebook?.selectedTimeslot}
                          onClick={async () => {
                            try {
                              await handlePrebookTimeslot(
                                selectedOrder.deliveryService,
                                selectedOrder?.shopId,
                                requiresAddressLine ? constructAddressLine(selectedOrder?.address) : undefined
                              );
                              setShowTimeslots(false);
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </AccordionContent>
            )}
          </div>
        </div>

        <Checkout
          headerTitle={t('common.asides.edit_order.payment_details')}
          type={'PaymentWay'}
          paymentDropdownOptions={paymentDropdownOptions ?? []}
          agree={details}
          setAgree={() => setDetails(!details)}
          invoiceTitle={t('checkout.invoice_title')}
          dropdownTitle={t('checkout.payment_dropdown_title')}
          paymentIndex={paymentIndex}
          onPaymentChange={i => choosePaymentOption(i)}
          saveCard={saveCard}
          setSaveCard={setSaveCard}
          buttonText={t('common.asides.save_btn')}
        />

        <div className={styles.section}>
          <Heading type={HeadingEnum.HeadingXS} bold={BoldEnum.Bold} classOption={styles.sectionTitle}>
            {t('common.asides.edit_order.order_preferences')}
          </Heading>

          <div className={styles.sectionContent}>
            <TextArea
              title={t('common.asides.edit_order.comment_title')}
              className={styles.textArea}
              description={t('common.asides.edit_order.card_placeholder')}
              maxLength={1000}
              value={comment}
              onChangeValue={setComment}
              rows={4}
              id={'comments'}
            ></TextArea>
          </div>
        </div>

        <div className={styles.priceSection}>
          <PriceBreakDownDetails
            selectedOrder={checkDetailsOnly ? updatedOrder : fullCartStore.getValue()?.data?.order}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          text={t('common.asides.edit_order.complete_btn_label')}
          onClick={() => (checkDetailsOnly ? handleCheckDetailsSubmit() : handleSubmit())}
        ></Button>
      </div>
    </div>
  );
}
